import { useMemo } from 'react';
import {
  isCGDOLoadingResponse,
  hasNoLoadedData,
  hasCGDOData,
} from 'app/shared/utils/fastArchHelpers';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';

/**
 * Shared hook for progressive rendering of CGDO (Cold Storage Data Objects) detail data.
 *
 * Convention:
 * - isLoading: Indicates if PRIMARY CONTENT (main entity/instrument/transaction data) is loading
 *   - true: Primary content is still loading (no main data available yet)
 *   - false: Primary content is loaded (main data exists, even if associations are loading)
 * - coldStorageLoading: Indicates if ASSOCIATIONS (transactions, alerts, etc.) are loading
 *   - Use this for section-specific loading indicators (e.g., ColdStorageCGDOMessage)
 *
 * This separation allows:
 * - Progressive rendering: Main data shows immediately when available
 * - Section-specific loading: Use coldStorageLoading for association sections
 *
 * @template TData - The main response type (e.g., FullEntityResponse, FullInstrumentResponse)
 * @template TSelect - The selected/transformed data type (defaults to TData)
 *
 * @param queryData - The query data which may be TData, TData & CGDOLoadingResponse, or CGDOLoadingResponse
 * @param isQueryLoading - Whether the query is currently loading
 * @param isQueryError - Whether the query encountered an error
 * @param select - Function to transform/select data from TData to TSelect (defaults to identity function)
 *
 * @returns Object with `data` and `isLoading` properties
 *   - data: The main data if available (even if associations are loading)
 *   - isLoading: true only if primary content is loading (main data not available yet)
 */
export function useCGDOProgressiveData<
  TData extends { id?: string | number; external_id?: string },
  TSelect = TData,
>(
  queryData:
    | TData
    | (TData & CGDOLoadingResponse)
    | CGDOLoadingResponse
    | undefined,
  isQueryLoading: boolean,
  isQueryError: boolean,
  select: (data: TData) => TSelect = (data) => data as unknown as TSelect,
): { data: TSelect | undefined; isLoading: boolean } {
  const isLoading = useMemo(() => {
    if (isQueryLoading) {
      return true;
    }

    // isLoading should only be true if primary content (main data) is loading
    // If main data exists, isLoading should be false (even if associations are loading)
    // Associations loading state is handled separately via coldStorageLoading
    if (hasCGDOData(queryData)) {
      return false; // Main data exists, so primary content is not loading
    }
    if (isCGDOLoadingResponse(queryData)) {
      return hasNoLoadedData(queryData); // No main data yet, still loading primary content
    }

    return false;
  }, [isQueryLoading, queryData]);

  const data: TSelect | undefined = useMemo(() => {
    if (isQueryError) {
      return undefined;
    }
    if (!queryData) {
      return undefined;
    }

    if (isCGDOLoadingResponse(queryData)) {
      if (hasCGDOData(queryData)) {
        const dataWithLoader = queryData as TData & CGDOLoadingResponse;
        return select(dataWithLoader);
      }
      return undefined;
    }

    // Not a loading response - cast and return
    return select(queryData as TData);
  }, [queryData, isQueryError, select]);

  return { data, isLoading };
}
